import { useQueryClient } from "@tanstack/react-query";

import { Card, CardContent } from "@/components/ui/card";

import { ProposalRow } from "../components/ProposalRow";
import { StatusStrip } from "../components/StatusStrip";
import { EmptyCard, QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import { invalidateAfterMutation, useApprovalsPage, useConfig } from "../data/queries";
import { useOpenMachineTab } from "../navigation";

const PREVIEW_SIZE = 10;

/**
 * What needs a decision right now.
 *
 * Meteoric's `/machine` home also carried Profit Yield and Units Sold headline
 * tiles, server-rendered by calling both of those APIs. That is deliberately
 * NOT reproduced here: Profit Yield and Units Sold are their own native modules
 * in this same area, one click away in the rail, and a third copy of two
 * dashboards is three places for the same number to be different. The tiles
 * were a workaround for a surface that had no shell around it; this one has a
 * shell.
 *
 * What survives is the half that is genuinely Machine's: the outstanding-work
 * counts, and the top of the review queue with the decision one click away.
 */
export function HomeTab() {
  const queryClient = useQueryClient();
  const openTab = useOpenMachineTab();
  const config = useConfig();
  const page = useApprovalsPage({ scope: "review", status: "proposed", limit: PREVIEW_SIZE, offset: 0 });

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Home"
        description="What the machine is waiting on you for. Profit Yield and Units Sold are their own modules in this area."
        aside={<StatusStrip />}
      />
      {page.isPending ? (
        <TabSkeleton rows={2} />
      ) : page.isError ? (
        <QueryErrorState title="Could not load the review queue" onRetry={() => page.refetch()} />
      ) : page.data.items.length === 0 ? (
        <EmptyCard label="Nothing is waiting for a decision." />
      ) : (
        <Card>
          <CardContent className="flex flex-col">
            {page.data.items.map((proposal) => (
              // `compact` + `forceDetail`: this row does not show the impact
              // panels, so a profit-risk approval is sent to the Review queue
              // rather than taken here.
              <ProposalRow
                key={proposal.proposal_id}
                proposal={proposal}
                compact
                forceDetail
                onOpenReviewQueue={() => openTab("review")}
                campaignTemplateDefault={config.data?.campaign_template_default}
                campaignTemplates={config.data?.campaign_templates}
                onDecided={() => void invalidateAfterMutation(queryClient)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
